import type { z } from "zod";

// A tRPC procedure is declared once, here, in the module's contract: its name,
// its kind, its input and its output. The process half binds a permission and
// a handler to it; the browser half derives its client from it. Neither side
// imports the other, and no router type ever crosses into the browser.

export type ProcedureKind = "query" | "mutation";

export type ProcedureDeclaration<
  TKind extends ProcedureKind = ProcedureKind,
  TInput extends z.ZodType = z.ZodType,
  TOutput extends z.ZodType = z.ZodType,
> = {
  readonly kind: TKind;
  readonly input: TInput;
  readonly output: TOutput;
  /** A mutation on a person's own credentials or keys (password, email, passkeys, sessions,
   * linked sign-ins, API keys, deleting the account): refused during an impersonation visit,
   * since it is the customer's alone (ADR-005 point 6, revision 1). */
  readonly ownCredential?: boolean;
};

export type ProcedureMap = Readonly<Record<string, ProcedureDeclaration>>;

export type TrpcContract<
  TNamespace extends string = string,
  TProcedures extends ProcedureMap = ProcedureMap,
> = {
  readonly kind: "trpc-contract";
  readonly namespace: TNamespace;
  readonly procedures: TProcedures;
};

/** What a caller sends. */
export type ProcedureInput<TProcedure extends ProcedureDeclaration> = z.input<TProcedure["input"]>;

/** What a caller receives, after the server has validated it. */
export type ProcedureOutput<TProcedure extends ProcedureDeclaration> = z.output<TProcedure["output"]>;

type Schemas<TInput extends z.ZodType, TOutput extends z.ZodType> = {
  readonly input: TInput;
  readonly output: TOutput;
};

export function query<TInput extends z.ZodType, TOutput extends z.ZodType>(
  schemas: Schemas<TInput, TOutput>,
): ProcedureDeclaration<"query", TInput, TOutput> {
  return { kind: "query", input: schemas.input, output: schemas.output };
}

export function mutation<TInput extends z.ZodType, TOutput extends z.ZodType>(
  schemas: Schemas<TInput, TOutput> & { readonly ownCredential?: boolean },
): ProcedureDeclaration<"mutation", TInput, TOutput> {
  return {
    kind: "mutation",
    input: schemas.input,
    output: schemas.output,
    ...(schemas.ownCredential ? { ownCredential: true } : {}),
  };
}

/** One namespace per module: `waitlist.join` belongs to waitlist and nobody else. */
export function defineTrpcContract<const TNamespace extends string, const TProcedures extends ProcedureMap>(
  namespace: TNamespace,
  procedures: TProcedures,
): TrpcContract<TNamespace, TProcedures> {
  return Object.freeze({ kind: "trpc-contract", namespace, procedures });
}
